/*
  使用例

  import { get, post, createApiUrl, RequestError } from './utils/request'

  try {
    // 型安全なAPI呼び出し
    const rooms = await get<Room[]>(sessionToken)(createApiUrl('/rooms'))

    const newRoom = await post<Room>(sessionToken)(
      createApiUrl('/rooms'),
      { name: 'New Room' }
    )

  } catch (error) {
    if (error instanceof RequestError) {
      console.error(`API Error [${error.status}]: ${error.message}`)
      // 特定のエラーコードに応じた処理
      if (error.status === 401) {
        // 認証エラー処理
      }
    } else {
      console.error('Unexpected error:', error)
    }
  }
*/
// Get API URL from environment variable (set at build time)
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787/api/v1';
class RequestError extends Error {
    status;
    code;
    constructor(message, status, code) {
        super(message);
        this.name = 'RequestError';
        this.status = status;
        this.code = code;
    }
}
const createHeaders = (sessionToken) => {
    const hd = { 'Content-Type': 'application/json' };
    if (sessionToken) {
        hd['Authorization'] = 'Bearer ' + sessionToken;
    }
    return hd;
};
const handleResponse = async (response) => {
    const contentType = response.headers.get('content-type');
    if (!response.ok) {
        let errorMessage = `HTTP Error: ${response.status} ${response.statusText}`;
        let errorCode;
        try {
            if (contentType?.includes('application/json')) {
                const errorData = await response.json();
                errorMessage = errorData.message || errorData.error || errorMessage;
                errorCode = errorData.code;
            }
            else {
                const errorText = await response.text();
                if (errorText) {
                    errorMessage = errorText;
                }
            }
        }
        catch {
            // JSON解析に失敗した場合はデフォルトメッセージを使用
        }
        throw new RequestError(errorMessage, response.status, errorCode);
    }
    try {
        if (contentType?.includes('application/json')) {
            return await response.json();
        }
        else {
            // JSONでない場合はテキストとして返す
            return await response.text();
        }
    }
    catch (error) {
        throw new RequestError('Failed to parse response', response.status);
    }
};
export const createApiUrl = (url) => {
    return API_BASE_URL + url;
};
export function get(sessionToken) {
    return async function (url) {
        try {
            const response = await fetch(url, {
                method: 'GET',
                headers: createHeaders(sessionToken)
            });
            return await handleResponse(response);
        }
        catch (error) {
            if (error instanceof RequestError) {
                throw error;
            }
            throw new RequestError('Network error occurred', 0);
        }
    };
}
export function post(sessionToken) {
    return async function (url, body) {
        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: createHeaders(sessionToken),
                body: JSON.stringify(body)
            });
            return await handleResponse(response);
        }
        catch (error) {
            if (error instanceof RequestError) {
                throw error;
            }
            throw new RequestError('Network error occurred', 0);
        }
    };
}
export function patch(sessionToken) {
    return async function (url, body) {
        try {
            const response = await fetch(url, {
                method: 'PATCH',
                headers: createHeaders(sessionToken),
                body: JSON.stringify(body)
            });
            return await handleResponse(response);
        }
        catch (error) {
            if (error instanceof RequestError) {
                throw error;
            }
            throw new RequestError('Network error occurred', 0);
        }
    };
}
export function deleteMethod(sessionToken) {
    return async function (url) {
        try {
            const response = await fetch(url, {
                method: 'DELETE',
                headers: createHeaders(sessionToken)
            });
            return await handleResponse(response);
        }
        catch (error) {
            if (error instanceof RequestError) {
                throw error;
            }
            throw new RequestError('Network error occurred', 0);
        }
    };
}
// エラークラスとインターフェースもエクスポート
export { RequestError };
